import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import MaterialAutocomplete, {
  AutocompleteProps as MuiAutocompleteProps,
} from '@mui/material/Autocomplete';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import { ArrowDownIconThin } from '@buoyhealth/common.buoy-icons';

const StyledAutocomplete = styled(MaterialAutocomplete)`
  label {
    color: ${({ theme }) => theme.palette.gray[70]};
    font-size: 16px;

    &.MuiInputLabel-shrink {
      font-size: 14px;
    }
  }
  input {
    ${({ theme }) => theme.typography.variants.primary.main};
    font-size: 16px;
  }

  .MuiAutocomplete-endAdornment {
    margin-right: 6px;
  }
`;

const StyledCollapse = styled(Collapse)`
  .MuiPaper-root {
    padding: 8px 16px;

    li {
      min-height: 46px;
      border-bottom: ${({ theme }) => `1px solid ${theme.palette.gray[20]}`};
      ${({ theme }) => theme.typography.variants.primary.main};
      font-size: 16px;
      color: ${({ theme }) => theme.palette.primary.main};

      &:last-of-type {
        border: 0;
      }

      &:hover {
        color: ${({ theme }) => theme.palette.primary.dark};
      }
    }
  }
`;

export interface IOption {
  value: string;
  label: string;
}

interface AutocompleteProps
  extends Omit<
    Partial<MuiAutocompleteProps<unknown, false, false, boolean>>,
    'onChange' | 'renderTags'
  > {
  handleChange: (val: IOption) => void;
  handleInputChange?: (val: string) => void;
  icon?: React.ReactNode;
  label: string;
  options: IOption[];
}

const Autocomplete = ({
  handleInputChange,
  handleChange,
  icon,
  label,
  options,
  ...rest
}: AutocompleteProps) => {
  const [val, setValue] = useState<string>();
  const [inputVal, setInputVal] = useState<string>('');
  const [menuVisible, setMenuVisible] = useState(false);

  const onChange = (_e: React.SyntheticEvent, value: unknown) => {
    const option = value as IOption;
    setValue(option.value);
    handleChange(option);
  };

  const onInputChange = (_e: React.SyntheticEvent, value: string) => {
    setInputVal(value);
    handleInputChange?.(value);
  };

  const menuShouldAnimateIn = useMemo(() => {
    if (rest.openOnFocus && !menuVisible) {
      return true;
    }
    if (rest.loading && !options.length) {
      return true;
    }
  }, [menuVisible, options.length, rest.loading, rest.openOnFocus]);

  return (
    // FIXME
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <StyledAutocomplete
      PopperComponent={(props) => (
        <StyledCollapse
          appear={menuShouldAnimateIn}
          in={props.open && (!!options.length || rest.loading)}
          onEntered={() => setMenuVisible(true)}
        >
          {props.children as any}
        </StyledCollapse>
      )}
      clearIcon={null}
      componentsProps={{
        paper: {
          elevation: 0,
        },
      }}
      inputValue={inputVal}
      onChange={onChange}
      onClose={() => {
        setMenuVisible(false);
      }}
      onInputChange={onInputChange}
      options={options}
      popupIcon={icon || <ArrowDownIconThin size={24} />}
      renderInput={(params) => (
        <TextField {...params} InputLabelProps={{ size: 'normal' }} label={label} />
      )}
      sx={{
        backgroundColor: 'white',
        borderRadius: '4px',
      }}
      value={val}
      {...rest}
    />
  );
};

export default Autocomplete;
